/** Copyright (c) 2017 T. Rowe Price. All rights reserved. */

import { toWords } from 'number-to-words';

import { currency, mmmddyyyy } from './Dates';
import { percentValue } from './Formats';

/**
 * Wraps a formatting function to return the formatted value inside a span.
 * @type {Function} Hash of formatting functions and their names
 */

const wrapFormatted = (fmt, wrapperClass) => {
  return (value, name) => {
    const fmtValue = fmt(value);
    return `<span class='${name} ${
      typeof wrapperClass == 'string' ? wrapperClass : wrapperClass(value)
    }'>${fmtValue}</span>`;
  };
};

export const posNegWrapper = (value) => {
  if (value > 0) {
    return 'value positive';
  } else if (value < 0) {
    return 'value negative';
  }
  return 'value';
};

const emphasisWrapper = () => {
  return 'value emphasize';
};

export const interpolateFormatters = {
  inceptionDate: mmmddyyyy,
  oneYearReturn: wrapFormatted(percentValue, posNegWrapper),
  inceptionReturn: wrapFormatted(percentValue, posNegWrapper),
  numOfAdditions: wrapFormatted(toWords, 'value'),
  numOfDeductions: wrapFormatted(toWords, 'value'),
  changeValue: wrapFormatted(
    (value) => currency(Math.abs(value)),
    posNegWrapper
  ),
  netInvestment: wrapFormatted(currency, posNegWrapper),
  endingMarketValue: wrapFormatted(currency, posNegWrapper),
  absMarketValueChange: wrapFormatted(currency, emphasisWrapper),
  absMarketValueChangeMinusNetInvestment: wrapFormatted(
    currency,
    emphasisWrapper
  )
};

/**
 * Dynamically replace placeholders in a string with their values from parameters,
 * optionally formatting and decorating values.
 *
 * @param template The template with embedded placeholders.
 * @param params The values from which to put in the placeholders. Can be a simple value like ${change} or can include a
 *               formatting function like ${change,currency} or ${change,percent}.
 *
 * @returns string Formatted string.
 */
export const interpolate = (template, params) => {
  if (typeof template !== 'string') {
    console.error('Cannot interpolate non-string: ' + template, params);
    return '';
  }

  if (params === undefined) {
    console.error('Cannot interpolate without parameter values: ' + template);
    return '';
  }

  // RegEx:
  // Looking for ${name,format}
  // name is required and captured
  // the command and format is optional, but only the format is captured (excludes the comma)
  return template.replace(/\${(\w+)(?:,(\w+))?}/g, interpolateReplacer);

  function interpolateReplacer(placeholder, name) {
    const rawValue = params[name];
    if (rawValue === undefined) {
      console.error('Template defined placeholder but no value was provided.', {
        template,
        params,
        placeholder,
        name
      });
      return '';
    }

    let formatted = rawValue;
    const formatter = interpolateFormatters[name];
    if (formatter) {
      formatted = formatter(rawValue, name);
    }

    return formatted;
  }
};
